import { useEffect, useState } from "react";
import { Rocket, Sparkles, Clock, Crown } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";

function remaining(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "Expired";
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m remaining`;
}

export default function Boost() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/boost").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  if (!data) return <FullSpinner />;

  const start = async (kind) => {
    setBusy(kind);
    try { await api.post("/boost", { kind }); toast.success(`${kind} activated`); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="boost-page">
      <h1 className="font-serif text-3xl font-bold text-[#58111A] flex items-center gap-2"><Rocket className="w-6 h-6 text-[#C59B27]" /> Boost & Spotlight</h1>
      {data.active && (
        <div className="mj-card p-5 border-l-4 border-l-[#C59B27]" data-testid="active-boost">
          <div className="flex items-center justify-between">
            <div><div className="font-serif text-lg font-semibold text-[#781826] capitalize">{data.active.kind} active</div>
              <div className="text-xs text-[#605255]">Started {new Date(data.active.started_at).toLocaleString()}</div></div>
            <div className="text-sm font-semibold text-[#15803D] flex items-center gap-1"><Clock className="w-4 h-4" /> {remaining(data.active.ends_at)}</div>
          </div>
        </div>
      )}
      {data.quota && (
        <div className="mj-card p-4 flex items-center justify-between" data-testid="boost-quota">
          <div>
            <div className="text-sm font-semibold text-[#261A1C]">Monthly boosts</div>
            <div className="text-xs text-[#605255] capitalize">{data.quota.plan} plan · renews on a rolling 30-day window</div>
          </div>
          <div className="text-right">
            <div className="font-serif text-2xl font-bold text-[#781826]" data-testid="boost-quota-remaining">{data.quota.remaining}<span className="text-sm text-[#9E7B1B]">/{data.quota.limit}</span></div>
            <div className="text-[11px] text-[#605255]">remaining</div>
          </div>
        </div>
      )}
      {!data.eligible && (
        <div className="mj-card p-4 text-sm text-[#9E7B1B] flex items-center gap-2"><Crown className="w-4 h-4" /> Boost is a Premium feature. <a href="/app/subscription" className="text-[#781826] font-semibold underline">Upgrade for ₹150/month</a></div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="mj-card p-6">
          <Rocket className="w-8 h-8 text-[#781826] mb-3" />
          <h3 className="font-serif text-xl font-semibold text-[#261A1C]">Boost</h3>
          <p className="text-sm text-[#605255] mt-1">Appear higher in recommendations for {data.settings?.boost_hours || 24} hours.</p>
          <button onClick={() => start("boost")} disabled={busy || !data.eligible || (data.quota && data.quota.remaining <= 0)} className="btn-primary w-full mt-4 disabled:opacity-60" data-testid="start-boost">{busy === "boost" ? "…" : (data.quota && data.quota.remaining <= 0 ? "Quota used up" : "Activate Boost")}</button>
        </div>
        <div className="mj-card p-6 bg-gradient-to-b from-[#FAF5EE] to-white border-2 border-[#C59B27]">
          <Sparkles className="w-8 h-8 text-[#C59B27] mb-3" />
          <h3 className="font-serif text-xl font-semibold text-[#261A1C]">Spotlight</h3>
          <p className="text-sm text-[#605255] mt-1">Featured placement in discovery for {data.settings?.spotlight_hours || 72} hours.</p>
          <button onClick={() => start("spotlight")} disabled={busy || !data.eligible || (data.quota && data.quota.remaining <= 0)} className="btn-gold w-full mt-4 py-2.5 rounded-xl disabled:opacity-60" data-testid="start-spotlight">{busy === "spotlight" ? "…" : (data.quota && data.quota.remaining <= 0 ? "Quota used up" : "Activate Spotlight")}</button>
        </div>
      </div>
      <p className="text-xs text-[#9E7B1B]">Boost increases visibility only. It does not guarantee matches or responses.</p>
      {data.history.length > 0 && (
        <div className="mj-card p-5">
          <h2 className="font-serif text-lg font-semibold text-[#261A1C] mb-2">Boost History</h2>
          {data.history.map((b, i) => (
            <div key={i} className="flex justify-between py-2 border-b border-[#F1E9DA] last:border-0 text-sm">
              <span className="capitalize text-[#261A1C]">{b.kind}</span>
              <span className="text-[#605255]">{new Date(b.started_at).toLocaleDateString()} → {new Date(b.ends_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
